import React, { type ChangeEvent, useState, useRef } from 'react';

import { TextField, TextPassage } from '@petsmart-ui/sparky';
import { type Suggestions } from '@libs/experian';
import {
    getFormattedAddress,
    getStreetAddressData,
} from '@libs/experian/server';
import type { SddForm } from '@app/pdp/components/AddToCart/DeliveryMethods/types';
import { clsx, useOnClickOutside } from '@libs/common-next';
import { Locale, getLocale } from '@libs/core';

export type StreetAddressInputProps = {
    sddOnChange: ({
        target: { name, value },
    }: ChangeEvent<HTMLInputElement>) => void;
    sddForm: SddForm;
    isError?: boolean;
    validationMsg?: string | null;
};

export default function StreetAddressInput({
    sddOnChange,
    sddForm,
    isError,
    validationMsg = null,
}: StreetAddressInputProps) {
    const [suggestions, setSuggestions] = useState<Suggestions[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    const clearSuggestions = () => {
        setSuggestions([]);
    };

    useOnClickOutside(containerRef, clearSuggestions);
    const streetAddressEvent = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length === 0) {
            setSuggestions([]);
        }
        sddOnChange({
            target: { name: 'street', value: event.target.value },
        } as ChangeEvent<HTMLInputElement>);

        if (event.target.value.length < 2) {
            return;
        }
        const streetAddressInput = event.target.value;
        const data = await getStreetAddressData(streetAddressInput);
        if (!data) {
            return;
        }
        setSuggestions(data.result.suggestions);
    };

    const setAddress = async (streetAddressKey: string) => {
        setSuggestions([]);
        const addressResponse = await getFormattedAddress(streetAddressKey);

        if (!addressResponse) {
            return;
        }
        const { address } = addressResponse.result;
        const street =
            address.address_line_1 +
            address.address_line_2 +
            address.address_line_3;

        let zip = address.postal_code;

        if (getLocale() === Locale['en-US']) {
            zip = address.postal_code.substring(
                0,
                address.postal_code.indexOf('-')
            );
        }

        const state = address.region;
        const city = address.locality;

        Object.entries({ street, state, city, zip }).forEach(([name, value]) =>
            sddOnChange({
                target: { name, value },
            } as ChangeEvent<HTMLInputElement>)
        );
    };

    return (
        <div className="sdd-form-input__street-container" ref={containerRef}>
            <TextField
                onChange={streetAddressEvent}
                data-cs-mask
                name="street"
                data-testid="sdd-street"
                className="sdd-form-input"
                label="Street address"
                value={sddForm.street}
                id="street"
                isError={isError}
                type="text"
                fieldNote={validationMsg}
            />
            <div
                className={clsx(
                    'sdd-form-input__street-dropdown',
                    suggestions.length === 0 &&
                        'sdd-form-input__street-dropdown--hidden'
                )}
            >
                {suggestions.map(suggestion => (
                    <TextPassage
                        data-cs-mask
                        key={suggestion.global_address_key}
                        onClick={() =>
                            setAddress(suggestion.global_address_key)
                        }
                        className="sdd-form-input__street-dropdown__item"
                    >
                        {suggestion.text}
                    </TextPassage>
                ))}
            </div>
        </div>
    );
}
